import { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useTenantStore } from '../stores/tenant.store';
import { usePermissions } from './use-permissions';

// ─── Step / stage definitions ────────────────────────────────────────────────
// `key` matches an i18n key under setup:steps.<key>; `href` deep-links to the
// real page that completes it; `optional` steps don't count toward the % (they
// are recommendations, so the essential bar can reach 100%).

interface StepDef {
  key: string;
  href: string;
  optional?: boolean;
}
interface StageDef {
  key: string;
  steps: StepDef[];
}

const STAGE_DEFS: StageDef[] = [
  {
    key: 'identity',
    steps: [
      { key: 'color', href: '/settings?tab=identity' },
      { key: 'logo', href: '/settings?tab=identity' },
      { key: 'mission', href: '/settings?tab=framework' },
    ],
  },
  {
    key: 'site',
    steps: [
      { key: 'landing', href: '/settings?tab=public' },
      { key: 'domain', href: '/settings?tab=public', optional: true },
    ],
  },
  {
    key: 'academic',
    steps: [
      { key: 'department', href: '/academic?tab=departments' },
      { key: 'program', href: '/academic?tab=programs' },
      { key: 'course', href: '/academic?tab=courses' },
      { key: 'term', href: '/academic?tab=terms' },
      { key: 'courseTemplate', href: '/plantillas-syllabus', optional: true },
      { key: 'textbook', href: '/catalogo-libros', optional: true },
    ],
  },
  {
    key: 'admissions',
    steps: [
      { key: 'formTemplate', href: '/form-templates' },
      { key: 'admission', href: '/admissions', optional: true },
    ],
  },
  {
    key: 'team',
    steps: [{ key: 'team', href: '/usuarios' }],
  },
  {
    key: 'operations',
    steps: [
      { key: 'section', href: '/sections', optional: true },
      { key: 'enrollment', href: '/sections', optional: true },
      { key: 'tuition', href: '/tuition', optional: true },
      { key: 'scholarship', href: '/scholarships', optional: true },
    ],
  },
];

export interface SetupStep extends StepDef {
  done: boolean;
}
export interface SetupStage {
  key: string;
  steps: SetupStep[];
}
export interface SetupProgress {
  loading: boolean;
  pct: number;
  doneCount: number;
  totalCount: number;
  stages: SetupStage[];
  /** Href of the first pending essential step — where "Continue" jumps to. */
  nextHref: string | null;
}

// ─── Detection ────────────────────────────────────────────────────────────────

/** Existence check: any doc in a tenant-scoped collection. Errors → false. */
async function any(tid: string, sub: string): Promise<boolean> {
  try {
    const snap = await getDocs(query(collection(db, `tenants/${tid}/${sub}`), limit(1)));
    return !snap.empty;
  } catch {
    return false;
  }
}

async function hasTeam(tid: string): Promise<boolean> {
  try {
    const snap = await getDocs(
      query(
        collection(db, `tenants/${tid}/memberships`),
        where('roles', 'array-contains-any', ['faculty', 'registrar']),
        limit(1),
      ),
    );
    return !snap.empty;
  } catch {
    return false;
  }
}

async function detect(tid: string): Promise<Record<string, boolean>> {
  let cfg: Record<string, unknown> = {};
  try {
    const tdoc = await getDoc(doc(db, 'tenants', tid));
    cfg = ((tdoc.data()?.['config'] ?? {}) as Record<string, unknown>) || {};
  } catch {
    /* config unreadable → treated as not set */
  }
  const mission = cfg['mission'] as { text?: string } | undefined;
  const policies = cfg['policies'] as unknown[] | undefined;
  const landing = cfg['landing'] as { heroTitle?: string; about?: string } | undefined;
  const domains = cfg['customDomains'] as { landing?: string } | undefined;

  const [
    department,
    program,
    course,
    term,
    courseTemplate,
    textbook,
    formTemplate,
    admission,
    section,
    enrollment,
    tuition,
    scholarship,
    team,
  ] = await Promise.all([
    any(tid, 'departments'),
    any(tid, 'programs'),
    any(tid, 'courses'),
    any(tid, 'terms'),
    any(tid, 'courseTemplates'),
    any(tid, 'textbooks'),
    any(tid, 'formTemplates'),
    any(tid, 'admissions'),
    any(tid, 'sections'),
    any(tid, 'enrollments'),
    any(tid, 'tuitionPayments'),
    any(tid, 'scholarships'),
    hasTeam(tid),
  ]);

  return {
    color: !!cfg['primaryColor'],
    logo: !!cfg['logoUrl'],
    mission: !!mission?.text,
    policies: Array.isArray(policies) && policies.length > 0,
    landing: !!landing && (!!landing.heroTitle || !!landing.about),
    domain: !!domains?.landing,
    department,
    program,
    course,
    term,
    courseTemplate,
    textbook,
    formTemplate,
    admission,
    team,
    section,
    enrollment,
    tuition,
    scholarship,
  };
}

// Module-level cache so the always-mounted sidebar widget doesn't re-query on
// every navigation (AppShell remounts per route). The page forces a refresh.
const CACHE_TTL_MS = 30_000;
const cache = new Map<string, { at: number; signals: Record<string, boolean> }>();

// ─── Hook ───────────────────────────────────────────────────────────────────

/**
 * Live setup progress for the current tenant. Computes each step from data
 * (config fields + collection existence) so it stays in sync as the admin
 * completes work — no manual checkboxes. The % counts only essential steps.
 *
 * @param opts.fresh bypass the TTL cache and always re-detect on mount.
 */
/** Quién puede disparar la detección: SOLO la administración del seminario. */
export interface SetupAccess {
  permissionsReady: boolean;
  isAdmin: boolean;
}

/**
 * La detección del avance, con su guarda (S0·SEC-1a·0). `detect()` cuenta
 * «¿hay al menos uno?» en 13 colecciones con `limit(1)` y SIN filtro: con un
 * alumno, las reglas le dejaban recibir la primera admisión, inscripción y
 * pago del seminario — de OTRA persona. El widget y el banner escondían el
 * resultado a quien no es admin, pero el hook ya había consultado: esconder no
 * es no leer. Por eso la guarda vive AQUÍ y no en cada llamador: sin permisos
 * resueltos o sin ser admin, no se toca Firestore. `null` = no se consultó.
 */
export async function loadSetupSignals(
  tenantId: string | null,
  access: SetupAccess,
): Promise<Record<string, boolean> | null> {
  if (!tenantId || !access.permissionsReady || !access.isAdmin) return null;
  return detect(tenantId);
}

export function useSetupProgress(opts?: { fresh?: boolean }): SetupProgress {
  const fresh = opts?.fresh ?? false;
  const currentTenantId = useTenantStore((s) => s.currentTenantId);
  const { isAdmin, permissionsReady } = usePermissions();
  const allowed = permissionsReady && isAdmin;
  const cached = allowed && currentTenantId ? cache.get(currentTenantId) : undefined;
  const cacheValid = !fresh && cached && Date.now() - cached.at < CACHE_TTL_MS;
  const [signals, setSignals] = useState<Record<string, boolean> | null>(
    cacheValid ? cached!.signals : null,
  );

  useEffect(() => {
    let alive = true;
    if (!currentTenantId || !allowed) {
      setSignals(null);
      return;
    }
    const hit = cache.get(currentTenantId);
    if (!fresh && hit && Date.now() - hit.at < CACHE_TTL_MS) {
      setSignals(hit.signals);
      return;
    }
    setSignals(null);
    void loadSetupSignals(currentTenantId, { permissionsReady, isAdmin }).then((s) => {
      if (!s) return;
      cache.set(currentTenantId, { at: Date.now(), signals: s });
      if (alive) setSignals(s);
    });
    return () => {
      alive = false;
    };
  }, [currentTenantId, fresh, allowed, permissionsReady, isAdmin]);

  const loading = signals === null;
  const stages: SetupStage[] = STAGE_DEFS.map((stage) => ({
    key: stage.key,
    steps: stage.steps.map((step) => ({ ...step, done: signals?.[step.key] ?? false })),
  }));

  const essential = stages.flatMap((s) => s.steps).filter((s) => !s.optional);
  const doneCount = essential.filter((s) => s.done).length;
  const totalCount = essential.length;
  const pct = totalCount === 0 ? 0 : Math.round((doneCount / totalCount) * 100);
  const nextHref = essential.find((s) => !s.done)?.href ?? null;

  return { loading, pct, doneCount, totalCount, stages, nextHref };
}
